import type { PropsWithChildren } from 'react';
import styled, { css } from 'styled-components';
import type { LocationCardProps as LocationCardComponentProps } from '@elfsight/widget-location-card';
import { LocationCard as LocationCardComponent } from '@elfsight/widget-location-card';
import type { WidgetBackgroundProps } from '@elfsight/widget-ui';
import { createReadableClass } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import { pick } from 'es-toolkit';
import { BODY1_TEXT_STYLE, TITLE1_TEXT_STYLE } from '../components/typography';
import { useWidgetDimensions } from '../widget-dimensions-provider';

type LocationCardProps = PropsWithChildren<{
  aside?: boolean;
  background: WidgetBackgroundProps;
  padding: [number, number];
  footerOffset?: number | false;
}>;

export function LocationCard({
  children,
  aside = false,
  background,
  padding,
  footerOffset
}: LocationCardProps) {
  const settings = useSettings();
  const { isMobile } = useWidgetDimensions();

  const { color: widgetBgColor, type: widgetBgType } = settings.background;
  const businessLogoColor =
    widgetBgType === 'color' && widgetBgColor !== 'transparent'
      ? widgetBgColor
      : '#fff';

  const locationCardOptions: Partial<LocationCardComponentProps> = {
    renderThumbnailMode: !aside && settings.renderThumbnailMode,
    businessHoursDisplay: 'onPopover',
    businessLogoStyle: 'overlap',
    businessLogoColor: businessLogoColor,
    businessAboutShortened: true,
    businessNameStyle: {
      ...TITLE1_TEXT_STYLE,
      ...(isMobile && { fontSize: 18 })
    },
    textStyle: {
      ...BODY1_TEXT_STYLE,
      ...(isMobile && { fontSize: 14 })
    },
    footerOffset: footerOffset || 60,
    alignment: 'left',
    verticalPadding: padding[0],
    horizontalPadding: padding[1],
    locationPhotoPosition: 'center',
    locationPhotoHorizontalAspectRatio: aside ? 0.33 : 0.25,
    background: background,
    textColor: settings.textColor,
    openStatusColor: settings.textColor,
    closedStatusColor: settings.textColor,
    closedSoonStatusColor: settings.textColor,
    iconsColor: settings.accentColor,
    businessName: settings.businessName,
    businessAbout: settings.businessAbout,
    contactsPhone: settings.businessContactsPhone,
    contactsEmail: settings.businessContactsEmail,
    contactsAddress: settings.businessContactsAddress,
    contactsWebsite: settings.businessContactsWebsite,
    contactsWhatsApp: settings.businessContactsWhatsApp,
    contactsInstagram: settings.businessContactsInstagram,
    locationPhotoImage: settings.businessPicture,
    specialIntervals: settings.specialIntervals,
    businessLogo: settings.businessLogo,
    timeZone: settings.timeZone,
    language: settings.language,
    ...pick(settings, [
      'dayMondayOpen',
      'dayMondayHours',
      'dayTuesdayOpen',
      'dayTuesdayHours',
      'dayWednesdayOpen',
      'dayWednesdayHours',
      'dayThursdayOpen',
      'dayThursdayHours',
      'dayFridayOpen',
      'dayFridayHours',
      'daySaturdayOpen',
      'daySaturdayHours',
      'daySundayOpen',
      'daySundayHours'
    ])
  };

  return (
    <FloatingContainer _aside={aside}>
      <LocationCardComponent
        {...locationCardOptions}
        footerComponent={children}
      />
    </FloatingContainer>
  );
}

const FloatingContainer = styled.div.attrs({
  className: createReadableClass({
    component: 'location-card',
    element: 'floating-container'
  })
})<{ _aside: boolean }>`
  position: relative;
  width: 100%;
  height: 100%;
  ${({ _aside }) =>
    _aside &&
    css`
      position: sticky;
      top: 40px;
      height: fit-content;
    `}
`;
